import { INDEXABLE_ALTERNATES } from "./indexing";
import { LOCALIZED_LANGS, type Lang } from "./i18n";

const SITE = "https://sunbedly.com";

// Reciprocal hreflang alternates for a page that exists at `path` (english,
// unprefixed) and at /<lang> + path for each language in `alternates`, plus a
// self-referencing canonical. Google requires every language variant to list
// every other variant, including itself and x-default.
//
// The canonical must point at the variant being served, which is the one
// thing the shared list cannot express: an English route passes no `lang`,
// a locale route passes its own. Search Console was reporting a hundred-plus
// "duplicate without user-selected canonical" pages before this existed,
// because six near-identical language variants gave Google nothing to pick
// between.
//
// A page served in a language that is not in `alternates` is a noindex page
// (see indexing.ts). It gets its canonical and nothing else: hreflang is a
// set of indexable equivalents, and a noindex page is not one of them, so the
// indexable variants never mention it and it never claims membership.
export function hreflangLinks(
  path: string,
  lang?: string,
  alternates: readonly Lang[] = LOCALIZED_LANGS,
) {
  const clean = path === "/" ? "" : path;
  const self = lang ? `${SITE}/${lang}${clean}` : `${SITE}${clean}` || SITE;
  const canonical = { rel: "canonical", href: self };
  if (lang && !alternates.includes(lang as Lang)) return [canonical];
  return [
    canonical,
    { rel: "alternate", hrefLang: "x-default", href: `${SITE}${clean}` },
    { rel: "alternate", hrefLang: "en", href: `${SITE}${clean}` },
    ...alternates.map((l) => ({
      rel: "alternate",
      hrefLang: l,
      href: `${SITE}/${l}${clean}`,
    })),
  ];
}

// For club, region and theme pages: only the indexable languages take part.
export function catalogueHreflang(path: string, lang?: string) {
  return hreflangLinks(path, lang, INDEXABLE_ALTERNATES);
}
